import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { routes } from './app.routes';
import { FeatureFlagsService, provideEnvironment, providePrivanotTheme, provideToast } from '@privanot/shared-ui/providers';
import { environment } from '@client/env/environment';
import { StaticTranslateLoader } from '@client/shared/util/translate-loader';
import { authInterceptor } from '@client/auth/interceptors/auth.interceptor';
import { EntraAuthService } from '@client/auth/services/entra-auth.service';
import {
    Activity,
    ArrowLeft,
    ArrowRight,
    Check,
    CheckCheck,
    ChevronDown,
    ChevronRight,
    CircleAlert,
    CircleCheck,
    CircleDashed,
    CircleEllipsis,
    CircleX,
    Clock,
    ClockAlert,
    Copy,
    Download,
    Eye,
    FileArchive,
    FileCheck,
    FileText,
    FileX,
    Folder,
    FolderKanban,
    FolderLock,
    FolderOpen,
    Globe,
    Hourglass,
    Inbox,
    Info,
    KeyRound,
    LoaderCircle,
    LogIn,
    LogOut,
    LUCIDE_ICONS,
    LucideIconProvider,
    MoonStar,
    Percent,
    RefreshCw,
    RotateCcw,
    RotateCcwKey,
    Search,
    Server,
    Settings,
    ShieldCheck,
    ShieldX,
    Sparkles,
    TriangleAlert,
    Upload,
    Users,
    WifiOff,
    X,
} from 'lucide-angular';

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        // In the packaged Electron app the service listens on a generated loopback port. This pulls it from the electron runtime changes the
        // environment variable before the hub connects, so BaseWebsocketHubClient picks up the right BASE_URL.
        provideAppInitializer(async () => {
            const flags = inject(FeatureFlagsService);
            const auth = inject(EntraAuthService);
            console.log('[boot] initializer starting; compiled BASE_URL =', environment.BASE_URL);
            console.log('[boot] window.privanotRuntime =', !!window.privanotRuntime);

            const url = await window.privanotRuntime?.getServiceBaseUrl?.();
            console.log('[boot] getServiceBaseUrl() returned:', url);

            if (url) {
                environment.BASE_URL = url;
                console.log('[boot] BASE_URL overridden to:', environment.BASE_URL);
            } else {
                console.warn('[boot] no runtime URL — keeping compiled BASE_URL:', environment.BASE_URL);
            }

            const managed = (await window.privanotRuntime?.isServiceManagedByApp?.()) ?? false;
            flags.setServiceManagedByApp(managed);
            console.log('[boot] serviceManagedByApp =', managed);

            // Settle Entra sign-in before the first navigation: the route guards read it, and in the hosted app MSAL
            // must consume the redirect response on /sign-in. The splash window never signs in.
            if (!window.connectivityCheck) {
                await auth.initialize();
            }
        }),
        provideRouter(routes, withComponentInputBinding()),
        // XHR backend (not withFetch): fetch doesn't report upload progress, which the file upload dialog shows.
        provideHttpClient(withInterceptors([authInterceptor])),
        providePrivanotTheme({}),
        {
            provide: LUCIDE_ICONS,
            multi: true,
            useValue: new LucideIconProvider({
                Activity,
                ArrowLeft,
                ArrowRight,
                Check,
                CheckCheck,
                ChevronDown,
                ChevronRight,
                CircleAlert,
                CircleCheck,
                CircleDashed,
                CircleEllipsis,
                CircleX,
                Clock,
                ClockAlert,
                Copy,
                Download,
                Eye,
                FileArchive,
                FileCheck,
                FileText,
                FileX,
                Folder,
                FolderKanban,
                FolderLock,
                FolderOpen,
                Globe,
                Hourglass,
                Inbox,
                Info,
                KeyRound,
                LoaderCircle,
                LogIn,
                LogOut,
                MoonStar,
                Percent,
                RefreshCw,
                RotateCcw,
                RotateCcwKey,
                Search,
                Server,
                Settings,
                ShieldCheck,
                ShieldX,
                Sparkles,
                TriangleAlert,
                Upload,
                Users,
                WifiOff,
                X,
            }),
        },
        provideEnvironment(environment),
        provideToast(),
        provideTranslateService({
            fallbackLang: 'en',
            loader: { provide: TranslateLoader, useClass: StaticTranslateLoader },
        }),
    ],
};
